import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AbstractBookmarkService,
  AbstractNavigationService,
  Content,
  ContentViewModel,
  EpisodeContentViewModel,
  getTitleSlugFromContentTitle,
  MovieContentViewModel,
  MpxEpisodeContentResponse,
  mpxIdNumberFromUrl,
  MpxMovieContentResponse,
  MpxProgramResponse,
  MpxResolverService,
  MpxSeries,
  MpxSeriesContentResponse,
  ObservableEpisode,
  ObservableMovie,
  ObservableSeason,
  ObservableSeries,
  SeasonContentViewModel,
  SeriesContentViewModel,
  ShallowEpisodeDependencies,
} from 'osn-shared';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

type UnionObservableContent = ObservableMovie | ObservableSeries | ObservableSeason | ObservableEpisode;

@Injectable({ providedIn: 'root' })
export class ObservableContentService {
  private readonly guidToObservableProgram: Record<string, UnionObservableContent> = {};

  constructor(
    private readonly mpxResolver: MpxResolverService,
    private readonly bookmarkService: AbstractBookmarkService,
    private readonly navigationService: AbstractNavigationService,
    private readonly translateService: TranslateService
  ) {
    bookmarkService.bookmarksChanged$.subscribe((guids) => {
      for (const guid of guids) {
        this.guidToObservableProgram[guid]?.updateBookmark();
      }
    });
    this.translateService.onLangChange.subscribe(() => {
      for (const guid of Object.keys(this.guidToObservableProgram)) {
        this.guidToObservableProgram[guid]?.updateRouterLink();
      }
    });
  }

  public getObservableProgram$(content: ContentViewModel | Content): UnionObservableContent {
    if (this.guidToObservableProgram[content.guid]) {
      return this.guidToObservableProgram[content.guid];
    }

    let result: UnionObservableContent;

    switch (content.type) {
      case 'movie':
        result = new ObservableMovie(
          content,
          (it) => this.fetchShallowContent$(it),
          (it) => this.fetchDetailedContent$(it),
          (it) => this.bookmarkService.getBookmarkOfContent$(content.guid, content.type),
          (it) => this.getRouterLink$(it)
        );
        break;
      case 'series':
        result = new ObservableSeries(
          content,
          (it) => this.fetchShallowContent$(it),
          (it) => this.fetchDetailedContent$(it),
          (it) => this.bookmarkService.getBookmarkOfContent$(content.guid, content.type),
          (it) => this.getRouterLink$(it)
        );
        break;
      case 'season':
        result = new ObservableSeason(
          content,
          (it) => this.fetchShallowContent$(it),
          (it) => this.fetchDetailedContent$(it),
          (it) => this.bookmarkService.getBookmarkOfContent$(content.guid, content.type),
          (it) => this.getRouterLink$(it)
        );
        break;
      case 'episode':
        result = new ObservableEpisode(
          content,
          (it) => this.fetchShallowContent$(it),
          (it) => this.fetchDetailedContent$(it),
          (it) => this.bookmarkService.getBookmarkOfContent$(content.guid, content.type),
          (it) => this.getRouterLink$(it)
        );
        break;
    }

    if (!result) {
      throw Error(`Content type could not be identified: ${JSON.stringify(result.content)}`);
    }

    this.guidToObservableProgram[content.guid] = result;

    return result;
  }

  private fetchShallowContent$(content: MovieContentViewModel): Observable<MpxMovieContentResponse>;
  private fetchShallowContent$(content: EpisodeContentViewModel): Observable<ShallowEpisodeDependencies>;
  private fetchShallowContent$(content: SeriesContentViewModel): Observable<MpxSeriesContentResponse>;
  private fetchShallowContent$(content: SeasonContentViewModel): Observable<MpxSeries>;
  private fetchShallowContent$(content: ContentViewModel): Observable<MpxProgramResponse | MpxSeries | ShallowEpisodeDependencies> {
    switch (content.type) {
      case 'movie':
      case 'series':
        return this.mpxResolver.resolveProgram$(content.guid);
      case 'episode':
        // @ts-ignore
        return this.mpxResolver
          .resolveProgram$(content.guid)
          .pipe(
            switchMap((result: MpxEpisodeContentResponse) =>
              this.mpxResolver.resolveProgram$(result.seriesId.split('/').last()).pipe(map((series) => ({ series, episode: result })))
            )
          );
      case 'season':
        return this.mpxResolver.resolveSeriesBySeasonGuid$(content.guid);
    }
  }

  private fetchDetailedContent$(content: MovieContentViewModel): Observable<MpxMovieContentResponse>;
  private fetchDetailedContent$(content: EpisodeContentViewModel | SeasonContentViewModel | SeriesContentViewModel): Observable<MpxSeries>;
  private fetchDetailedContent$(content: ContentViewModel): Observable<MpxMovieContentResponse | MpxSeries> {
    switch (content.type) {
      case 'movie':
        return this.mpxResolver.resolveProgram$(content.guid) as Observable<MpxMovieContentResponse>;
      case 'series':
        return this.mpxResolver.resolveSeriesWithSeasons$(content.guid);
      case 'episode':
        return this.mpxResolver
          .resolveProgram$(content.guid)
          .pipe(
            switchMap((program: MpxEpisodeContentResponse) =>
              this.mpxResolver.resolveSeriesWithSeasons$(mpxIdNumberFromUrl(program.seriesId))
            )
          );
      case 'season':
        return this.mpxResolver.resolveSeriesBySeasonGuid$(content.guid);
    }
  }

  private getRouterLink$(content: ContentViewModel): Observable<string> {
    // TODO [MO]: May need a smarter logic here to shallow load series if routerLink is needed in too many core places
    // TODO [MO]: ALSO todo: WTF is wrong with TS here

    const content$ =
      // @ts-ignore
      content.type === 'movie' || content.type === 'series' ? this.fetchShallowContent$(content) : this.fetchDetailedContent$(content);

    return content$.pipe(
      map((result: MpxSeries | MpxMovieContentResponse | MpxSeriesContentResponse) => {
        switch (content.type) {
          case 'movie': {
            const movie = result as MpxMovieContentResponse;
            const modifiedTitle = getTitleSlugFromContentTitle(movie.titleLocalized.en);
            return this.navigationService.getUrl('movieDetails', `${modifiedTitle}-${content.guid.toLowerCase()}`);
          }
          case 'series': {
            const series = result as MpxSeriesContentResponse;
            const modifiedTitle = getTitleSlugFromContentTitle(series.titleLocalized.en);
            return this.navigationService.getUrl('seriesDetails', `${modifiedTitle}-${content.guid.toLowerCase()}`);
          }
          case 'season': {
            const series = result as MpxSeries;
            const modifiedTitle = getTitleSlugFromContentTitle(series.series.titleLocalized.en);
            return this.navigationService.getUrl(
              'seasonDetails',
              series.series.guid,
              series.seasonsBySeasonGuid[content.guid].id.split('/').last().toLowerCase()
            );
          }
          case 'episode': {
            const series = result as MpxSeries;
            const modifiedTitle = getTitleSlugFromContentTitle(series.series.titleLocalized.en);
            return this.navigationService.getUrl(
              'episodeDetails',
              series.series.guid,
              series.seasonByEpisodeGuid[content.guid].id.split('/').last().toLowerCase(),
              content.guid.toLowerCase()
            );
          }
        }
      })
    );
  }
}
